import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import ErrorRetry from "@/components/ErrorRetry";
import { useClinic } from "@/context/ClinicContext";
import EmptyState from "@/components/EmptyState";
import BookingDrawer from "@/components/BookingDrawer";
import { useLocations } from "@/lib/useLocations";
import { activeLocations, isMultiLocation } from "@/lib/locations";
import AppointmentActions from "@/components/AppointmentActions";
import { Skeleton, TableSkeleton } from "@/components/Skeleton";
import { CalendarDays, Check, ChevronLeft, ChevronRight, Clock, Plus, UserX, Video } from "lucide-react";

function addDays(iso, n) {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function mondayOf(iso) {
  const d = new Date(`${iso}T12:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7;            // 0 = Monday
  return addDays(iso, -dow);
}

function dayHeading(iso) {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, {
    weekday: "short", month: "short", day: "numeric", timeZone: "UTC",
  });
}

function timeLabel(startUtc, tz) {
  return new Date(startUtc).toLocaleTimeString(undefined, {
    hour: "numeric", minute: "2-digit", timeZone: tz,
  });
}

/** How a row should render. Cancellations that were really MOVES must not read as
 *  cancellations — the calendar is where staff judge whether a patient is drifting. */
function rowStyle(a) {
  if (a.status === "cancelled" && a.cancellation_kind === "rescheduled")
    return { strike: false, chip: "Moved", tone: "bg-secondary text-muted-foreground", dim: true };
  if (a.status === "cancelled")
    return { strike: true, chip: "Cancelled", tone: "bg-secondary text-muted-foreground", dim: true };
  if (a.status === "no_show")
    return { strike: false, chip: "No-show", tone: "bg-amber-100 text-amber-800", dim: false, icon: UserX };
  if (a.status === "requested")
    return { strike: false, chip: "Requested", tone: "bg-amber-50 text-amber-700 border border-amber-200", dim: false, icon: Clock };
  if (a.status === "completed")
    return { strike: false, chip: "Completed", tone: "bg-emerald-100 text-emerald-800", dim: false, icon: Check };
  return { strike: false, chip: null, tone: "", dim: false };
}

export default function Schedule() {
  const { clinic } = useClinic();
  const tz = clinic?.timezone || "America/New_York";
  const today = new Date().toLocaleDateString("en-CA", { timeZone: tz });

  const [mode, setMode] = useState("day");
  const [anchor, setAnchor] = useState(today);
  const [providerId, setProviderId] = useState("");

  // Which site you are looking at survives moving between days and pages — front desk
  // sets it once at the start of a shift, not on every navigation.
  const { locations } = useLocations();
  const multiLocation = isMultiLocation(locations || []);
  const [locationId, setLocationId] = useState(
    () => sessionStorage.getItem("ros.scheduleLocation") || "");
  useEffect(() => {
    if (locationId) sessionStorage.setItem("ros.scheduleLocation", locationId);
    else sessionStorage.removeItem("ros.scheduleLocation");
  }, [locationId]);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState("");

  // Open slots are drawn for one visit type at a time: "what's free" has no answer
  // until you say free for what — a 20-minute injection and a 60-minute IV see very
  // different openings in the same afternoon.
  const [visitTypes, setVisitTypes] = useState(null);
  const [visitTypesErr, setVisitTypesErr] = useState(null);
  const [bookVtId, setBookVtId] = useState("");
  const [openSlots, setOpenSlots] = useState([]);
  const [slotsErr, setSlotsErr] = useState(null);
  const [drawer, setDrawer] = useState(null);

  const from = mode === "day" ? anchor : mondayOf(anchor);
  const to = mode === "day" ? anchor : addDays(mondayOf(anchor), 6);

  const load = useCallback(() => {
    setData(null);
    const params = { from, to, limit: 1000 };
    if (providerId) params.provider_id = providerId;
    api.get("/schedule", { params })
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => { setData({ appointments: [], total: 0, counts_by_status: {} }); setError(e); });
  }, [from, to, providerId]);

  useEffect(() => { load(); }, [load]);

  /* Was `.catch(() => setVisitTypes([]))`. An empty catalogue disables click-to-book with
     no explanation, so the calendar silently loses the ability to book at all and looks
     like a clinic that offers no visit types. */
  const loadVisitTypes = useCallback(() => {
    api.get("/visit-types")
      .then((r) => {
        setVisitTypes(r.data);
        setVisitTypesErr(null);
        setBookVtId((cur) => cur || r.data[0]?.id || "");
      })
      .catch((e) => { setVisitTypes([]); setVisitTypesErr(e); });
  }, []);

  useEffect(() => { loadVisitTypes(); }, [loadVisitTypes]);

  // Day view only. A week of open slots across every provider is thousands of chips and
  // nobody reads it; the week is for seeing shape, the day is for filling gaps.
  const loadOpenSlots = useCallback(() => {
    if (mode !== "day" || !bookVtId) { setOpenSlots([]); return; }
    const params = { visit_type_id: bookVtId, from, to };
    if (providerId) params.provider_id = providerId;
    // Scoped server-side: the availability engine already knows how to answer per site.
    if (locationId) params.location_id = locationId;
    /* Was `.catch(() => setOpenSlots([]))`. No open slots and "we could not find out
       which slots are open" look identical on a calendar, and the first reads as a fully
       booked day — so staff would turn a patient away from time that was actually free. */
    api.get("/availability", { params })
      .then((r) => { setOpenSlots(r.data.slots || []); setSlotsErr(null); })
      .catch((e) => { setOpenSlots([]); setSlotsErr(e); });
  }, [mode, bookVtId, from, to, providerId, locationId]);

  useEffect(() => { loadOpenSlots(); }, [loadOpenSlots]);

  const reloadAll = useCallback(() => { load(); loadOpenSlots(); }, [load, loadOpenSlots]);

  // The schedule feed has no location parameter, so the filter is applied here. Booked
  // rows carry location_id; anything written before locations existed has none and stays
  // visible rather than vanishing from a filtered calendar.
  const visibleAppointments = useMemo(() => {
    const rows = data?.appointments || [];
    if (!locationId) return rows;
    return rows.filter((a) => !a.location_id || a.location_id === locationId);
  }, [data, locationId]);

  const providers = useMemo(() => {
    const seen = new Map();
    visibleAppointments.forEach((a) => {
      if (a.provider_id && !seen.has(a.provider_id)) seen.set(a.provider_id, a.provider);
    });
    // A provider with an empty day still belongs in the filter — they are exactly the
    // one you are looking for when you need to fit someone in.
    openSlots.forEach((s) => {
      if (s.provider_id && !seen.has(s.provider_id)) seen.set(s.provider_id, s.provider_name);
    });
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [visibleAppointments, openSlots]);

  const days = useMemo(() => {
    const list = [];
    for (let d = from; d <= to; d = addDays(d, 1)) list.push(d);
    return list;
  }, [from, to]);

  // Provider columns within each day — the shape a clinic actually reads.
  const columns = useMemo(() => {
    const cols = new Map();
    const col = (id, name) => {
      const key = id || "unassigned";
      if (!cols.has(key)) cols.set(key, { id: key, name: name || "Unassigned", byDay: {}, freeByDay: {} });
      return cols.get(key);
    };
    visibleAppointments.forEach((a) => {
      const c = col(a.provider_id, a.provider);
      (c.byDay[a.date] = c.byDay[a.date] || []).push(a);
    });
    openSlots.forEach((s) => {
      const c = col(s.provider_id, s.provider_name);
      (c.freeByDay[s.date] = c.freeByDay[s.date] || []).push(s);
    });
    return [...cols.values()].sort((x, y) => x.name.localeCompare(y.name));
  }, [visibleAppointments, openSlots]);

  const requests = useMemo(
    () => visibleAppointments.filter((a) => a.status === "requested"),
    [visibleAppointments]);

  async function confirmRequest(a) {
    setConfirming(a.id); setError(null);
    try {
      await api.post(`/appointments/${a.id}/confirm`);
      reloadAll();   // confirming claims the slot, so the open times just changed
    } catch (e) {
      setError(e);
    } finally { setConfirming(""); }
  }

  const step = mode === "day" ? 1 : 7;

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Schedule</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {mode === "day" ? dayHeading(from) : `${dayHeading(from)} – ${dayHeading(to)}`}
            {data && ` · ${visibleAppointments.length} appointment${visibleAppointments.length === 1 ? "" : "s"}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border overflow-hidden">
            <button data-testid="schedule-day-toggle" onClick={() => setMode("day")}
                    className={`px-3 py-1.5 text-sm ${mode === "day" ? "bg-primary text-primary-foreground" : "bg-card"}`}>Day</button>
            <button data-testid="schedule-week-toggle" onClick={() => setMode("week")}
                    className={`px-3 py-1.5 text-sm ${mode === "week" ? "bg-primary text-primary-foreground" : "bg-card"}`}>Week</button>
          </div>
          <button data-testid="schedule-prev" onClick={() => setAnchor(addDays(anchor, -step))}
                  className="p-2 rounded-md border border-border bg-card"><ChevronLeft className="w-4 h-4" /></button>
          <button data-testid="schedule-today" onClick={() => setAnchor(today)}
                  className="px-3 py-1.5 rounded-md border border-border bg-card text-sm">Today</button>
          <button data-testid="schedule-next" onClick={() => setAnchor(addDays(anchor, step))}
                  className="p-2 rounded-md border border-border bg-card"><ChevronRight className="w-4 h-4" /></button>
          {providers.length > 1 && (
            <select data-testid="schedule-provider-filter" value={providerId}
                    onChange={(e) => setProviderId(e.target.value)}
                    className="text-sm border border-border rounded-md px-3 py-1.5 bg-card">
              <option value="">All providers</option>
              {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          {/* Progressive disclosure: a one-site clinic never sees this. */}
          {multiLocation && (
            <select data-testid="schedule-location-filter" value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                    className="text-sm border border-border rounded-md px-3 py-1.5 bg-card">
              <option value="">All locations</option>
              {activeLocations(locations).map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          )}
          <button data-testid="schedule-book-button" onClick={() => setDrawer({})}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium">
            <Plus className="w-4 h-4" /> Book
          </button>
        </div>
      </div>

      {mode === "day" && visitTypes?.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Show open times for</span>
          <select data-testid="schedule-book-visit-type" value={bookVtId}
                  onChange={(e) => setBookVtId(e.target.value)}
                  className="border border-border rounded-md px-3 py-1.5 bg-card">
            {visitTypes.map((v) => (
              <option key={v.id} value={v.id}>{v.name} · {v.duration_min}m</option>
            ))}
          </select>
          <span className="text-muted-foreground">— click one to book it.</span>
        </div>
      )}

      {/* The empty-appointments fallback makes a failed load look like an empty day. */}
      <ErrorRetry
        error={error}
        variant="inline"
        onRetry={() => { setError(null); load(); }}
        note="The calendar couldn't be loaded — this is not an empty day."
        testid="schedule-error"
      />
      <ErrorRetry
        error={visitTypesErr}
        variant="inline"
        onRetry={() => { setVisitTypesErr(null); loadVisitTypes(); }}
        note="Visit types couldn't be loaded, so click-to-book is unavailable until this works."
        testid="schedule-visit-types-error"
      />
      <ErrorRetry
        error={slotsErr}
        variant="inline"
        onRetry={() => { setSlotsErr(null); loadOpenSlots(); }}
        note="Open times couldn't be loaded. This day may have free slots that aren't shown — don't read it as fully booked."
        testid="schedule-slots-error"
      />

      {requests.length > 0 && (
        <div className="bg-card border border-amber-200 rounded-xl p-5" data-testid="requests-queue">
          <p className="font-medium text-sm inline-flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-amber-600" />
            {requests.length} booking request{requests.length === 1 ? "" : "s"} awaiting confirmation
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            These don't hold the slot until you confirm them.
          </p>
          <div className="mt-3 space-y-2">
            {requests.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-2">
                <span className="text-sm">
                  <span className="font-medium">{a.patient_name || "Patient"}</span> · {a.type} ·{" "}
                  {dayHeading(a.date)} {timeLabel(a.start_utc, tz)}
                </span>
                <button data-testid={`request-confirm-${a.id}`} disabled={confirming === a.id}
                        onClick={() => confirmRequest(a)}
                        className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
                  {confirming === a.id ? "Confirming…" : "Confirm"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {data === null ? <TableSkeleton rows={6} />
        : columns.length === 0 ? (
          <EmptyState icon={CalendarDays} title="Nothing booked in this range"
                      description="Move to another day or week, or clear the provider filter."
                      testid="schedule-empty" />
        ) : (
          <div className="overflow-x-auto">
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(240px, 1fr))` }}
                 data-testid="schedule-grid">
              {columns.map((col) => (
                <div key={col.id} className="space-y-3" data-testid={`schedule-column-${col.id}`}>
                  <p className="font-heading font-semibold text-sm sticky top-0 bg-background py-1">{col.name}</p>
                  {days.map((d) => {
                    const rows = (col.byDay[d] || []).slice()
                      .sort((a, b) => (a.start_utc || "").localeCompare(b.start_utc || ""));
                    const free = (col.freeByDay[d] || []).slice()
                      .sort((a, b) => (a.start_utc || "").localeCompare(b.start_utc || ""));
                    return (
                      <div key={d} className="space-y-1.5">
                        {mode === "week" && (
                          <p className="text-xs text-muted-foreground">{dayHeading(d)}</p>
                        )}
                        {rows.length === 0 && free.length === 0 && (
                          <p className="text-xs text-muted-foreground/60 border border-dashed border-border rounded-lg px-3 py-2">
                            No visits
                          </p>
                        )}
                        {rows.map((a) => {
                          const st = rowStyle(a);
                          return (
                            <div key={a.id} data-testid={`slot-${a.start_utc}`}
                                 className={`border border-border rounded-lg px-3 py-2 bg-card ${st.dim ? "opacity-60" : ""}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className={st.strike ? "line-through" : ""}>
                                  <p className="text-sm font-medium">{timeLabel(a.start_utc, tz)}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {a.patient_name || "Patient"} · {a.type}
                                  </p>
                                </div>
                                {st.chip && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 inline-flex items-center gap-1 ${st.tone}`}
                                        data-testid={`slot-status-${a.id}`}>
                                    {st.icon && <st.icon className="w-3 h-3" />}{st.chip}
                                  </span>
                                )}
                              </div>
                              {a.telehealth?.join_url && (
                                <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1 mt-1">
                                  <Video className="w-3 h-3" /> Telehealth
                                </span>
                              )}
                              <AppointmentActions appt={a} onChanged={reloadAll} className="mt-2" />
                            </div>
                          );
                        })}

                        {free.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 pt-0.5" data-testid={`open-slots-${col.id}-${d}`}>
                            {free.map((s) => (
                              <button key={s.start_utc} data-testid={`open-slot-${s.start_utc}`}
                                      onClick={() => setDrawer({
                                        providerId: s.provider_id, startUtc: s.start_utc,
                                        date: s.date, visitTypeId: bookVtId,
                                        // The slot you clicked already decided the site.
                                        locationId: s.location_id || "",
                                      })}
                                      className="px-2 py-1 rounded-md border border-dashed border-border text-xs text-muted-foreground hover:border-primary hover:text-primary hover:border-solid transition-all">
                                {timeLabel(s.start_utc, tz)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

      {data?.has_more && (
        <p className="text-xs text-muted-foreground" data-testid="schedule-truncated">
          Showing {data.appointments.length} of {data.total}. Narrow the range or filter by provider.
        </p>
      )}

      <BookingDrawer
        open={!!drawer}
        onClose={() => setDrawer(null)}
        onBooked={reloadAll}
        providerId={drawer?.providerId || ""}
        startUtc={drawer?.startUtc || ""}
        date={drawer?.date || (mode === "day" ? anchor : "")}
        visitTypeId={drawer?.visitTypeId || bookVtId}
        locationId={drawer?.locationId ?? locationId}
      />
    </div>
  );
}
